import React from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import ModuleDisabledState from "../../../components/dashboard/ModuleDisabledState";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";
import { projectsApi } from "../../../api/projects";
import { erpApi } from "../../../api/erp";
import { useProjects } from "../../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../../utils/projectModules";
import { resolveProjectNavigation } from "../../../utils/dashboardNavigation";
import { TRIAL_PRODUCTS_EVENT } from "../../../utils/trialOnboarding";
import {
  hasAnyProjectPermission,
  hasProjectPermission,
  normalizeProjectAccess,
} from "../../../utils/projectAccess";

function pathSegment(pathname) {
  return String(pathname || "").split("/").filter(Boolean).pop() || "";
}

function managementToolKey(pathname) {
  const segment = pathSegment(pathname);
  if (["ledger", "transacciones", "cuentas", "impuestos"].includes(segment)) return "accounting";
  if (segment === "items") return "items";
  if (segment === "movimientos") return "movements";
  if (segment === "caja") return "cash";
  if (segment === "stock") return "stock";
  return "overview";
}

function accountingToolPath(pathname) {
  const segment = pathSegment(pathname);
  return ["ledger", "transacciones", "cuentas", "impuestos"].includes(segment)
    ? segment
    : "ledger";
}

function InitialSetupGuide({ stage, actions = [] }) {
  if (!stage || stage.key === "ongoing") return null;
  const progressive = stage.key === "implementation";

  return (
    <Alert
      severity={stage.key === "operations" ? "success" : "info"}
      icon={stage.icon}
      sx={{
        borderRadius: 2.5,
        py: 0.35,
        minWidth: 0,
        flexDirection: progressive ? { xs: "column", sm: "row" } : "row",
        alignItems: progressive ? { xs: "stretch", sm: "center" } : "center",
        "& .MuiAlert-icon": progressive
          ? { alignSelf: { xs: "flex-start", sm: "center" }, mb: { xs: 0.35, sm: 0 } }
          : undefined,
        "& .MuiAlert-message": { width: "100%", py: 0.6, minWidth: 0 },
        "& .MuiAlert-action": progressive
          ? {
              width: { xs: "100%", sm: "auto" },
              alignItems: "center",
              ml: { xs: 0, sm: "auto" },
              pl: { xs: 0, sm: 2 },
              pt: 0.55,
              pb: 0.55,
              mr: 0,
            }
          : { alignItems: "center", pt: 0.55, pb: 0.55, mr: 0 },
      }}
      action={actions.length ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.7}
          sx={{ minWidth: { sm: "max-content" }, width: { xs: "100%", sm: "auto" } }}
        >
          {actions.map((action) => (
            <Button
              key={action.key}
              color="inherit"
              size="small"
              variant={action.primary ? "contained" : "text"}
              onClick={action.onClick}
              sx={{
                minHeight: progressive ? 42 : 34,
                width: { xs: progressive ? "100%" : "auto", sm: "auto" },
                textTransform: "none",
                fontWeight: 900,
                whiteSpace: "nowrap",
                boxShadow: "none",
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      ) : null}
    >
      <Stack spacing={0.15}>
        <Typography variant="caption" sx={{ fontWeight: 950, letterSpacing: 0.55, opacity: 0.78 }}>
          {progressive ? "PUESTA EN MARCHA · SIN BLOQUEOS" : `CONFIGURACIÓN INICIAL · PASO ${stage.step} DE 3`}
        </Typography>
        <Typography sx={{ fontWeight: 950, lineHeight: 1.25 }}>{stage.title}</Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
          {stage.detail}
        </Typography>
      </Stack>
    </Alert>
  );
}

export default function ProjectErpLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [access, setAccess] = React.useState(normalizeProjectAccess());
  const [bootstrap, setBootstrap] = React.useState({
    accounts: [],
    registers: [],
    taxProfiles: [],
    openSessions: [],
    progress: { itemCount: null, movementCount: null, confirmedMovementCount: null },
    implementationProgress: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [bootstrapping, setBootstrapping] = React.useState(false);
  const [bootstrapLoaded, setBootstrapLoaded] = React.useState(false);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const superAdmin = access.role === "superadmin";
  const enabled = superAdmin || isProjectModuleEnabled(resolvedProject, "system");
  const managementNavigation = React.useMemo(
    () => resolveProjectNavigation({
      project: resolvedProject,
      memberAccess: access,
      superAdmin,
      pathname: location.pathname,
      search: location.search,
    }).toolsByKey.system,
    [resolvedProject, access, superAdmin, location.pathname, location.search],
  );
  const resolvedPrimaryTools = managementNavigation?.secondaryTools || [];
  const visibleManagementTools = React.useMemo(
    () => resolvedPrimaryTools.filter((tool) => tool.visible),
    [resolvedPrimaryTools],
  );
  const resolvedAccountingTools = React.useMemo(
    () => resolvedPrimaryTools.find((tool) => tool.key === "accounting")?.secondaryTools || [],
    [resolvedPrimaryTools],
  );
  const visibleAccountingTools = React.useMemo(
    () => resolvedAccountingTools.filter((tool) => tool.visible),
    [resolvedAccountingTools],
  );
  const canBootstrap = hasAnyProjectPermission(access, [
    "erp.movements.read",
    "erp.ledger.read",
    "cash.registers.read",
    "erp.stock.read",
  ]);
  const canReadMoneyPlaces = hasProjectPermission(access, "cash.registers.read");
  const canManageMoneyPlaces = hasProjectPermission(access, "cash.registers.manage");
  const canReadLedger = hasProjectPermission(access, "erp.ledger.read");
  const canManageStock = hasProjectPermission(access, "erp.stock.manage");
  const canCreateItems = hasProjectPermission(access, "items.create");
  const canCreateSale = hasProjectPermission(access, "erp.sales.create");
  const canCreatePurchase = hasProjectPermission(access, "erp.purchases.create");
  const canCreateExpense = hasProjectPermission(access, "erp.expenses.create");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setBootstrapLoaded(false);
    setError("");
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setProject(data.project || null);
        setAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  const refreshBootstrap = React.useCallback(async () => {
    if (!projectId || !enabled || !canBootstrap) return null;
    try {
      setBootstrapping(true);
      const data = await erpApi.bootstrap(projectId);
      if (data.access) setAccess(normalizeProjectAccess(data.access));
      const next = {
        accounts: data.accounts || [],
        registers: data.registers || [],
        taxProfiles: data.taxProfiles || [],
        openSessions: data.openSessions || [],
        progress: {
          itemCount: data.progress?.itemCount === null || data.progress?.itemCount === undefined
            ? null
            : Number(data.progress.itemCount),
          movementCount: data.progress?.movementCount === null || data.progress?.movementCount === undefined
            ? null
            : Number(data.progress.movementCount),
          confirmedMovementCount: data.progress?.confirmedMovementCount === null || data.progress?.confirmedMovementCount === undefined
            ? null
            : Number(data.progress.confirmedMovementCount),
        },
        implementationProgress: data.implementationProgress
          ? {
              totalItems: data.implementationProgress.totalItems === null || data.implementationProgress.totalItems === undefined
                ? null
                : Number(data.implementationProgress.totalItems),
              stockTrackedItems: data.implementationProgress.stockTrackedItems === null || data.implementationProgress.stockTrackedItems === undefined
                ? null
                : Number(data.implementationProgress.stockTrackedItems),
              stockConfirmedItems: data.implementationProgress.stockConfirmedItems === null || data.implementationProgress.stockConfirmedItems === undefined
                ? null
                : Number(data.implementationProgress.stockConfirmedItems),
              stockPendingItems: data.implementationProgress.stockPendingItems === null || data.implementationProgress.stockPendingItems === undefined
                ? null
                : Number(data.implementationProgress.stockPendingItems),
              stockCountAvailable: data.implementationProgress.stockCountAvailable === true,
              activeStockCount: data.implementationProgress.activeStockCount || null,
            }
          : null,
      };
      setBootstrap(next);
      setBootstrapLoaded(true);
      return next;
    } catch (err) {
      setError(err.message || "No se pudo preparar la estructura del sistema");
      return null;
    } finally {
      setBootstrapping(false);
    }
  }, [projectId, enabled, canBootstrap]);

  React.useEffect(() => {
    if (!loading && enabled && canBootstrap) refreshBootstrap();
  }, [loading, enabled, canBootstrap, refreshBootstrap]);

  React.useEffect(() => {
    const handleProductsUpdated = (event) => {
      const eventProjectId = event?.detail?.projectId;
      if (eventProjectId && String(eventProjectId) !== String(projectId)) return;
      refreshBootstrap();
    };
    window.addEventListener(TRIAL_PRODUCTS_EVENT, handleProductsUpdated);
    return () => window.removeEventListener(TRIAL_PRODUCTS_EVENT, handleProductsUpdated);
  }, [projectId, refreshBootstrap]);

  const base = `/dashboard/proyectos/${projectId}/sistema`;
  const currentManagementTool = managementToolKey(location.pathname);
  const currentAccountingTool = accountingToolPath(location.pathname);
  const progress = bootstrap.progress || {};
  const implementationProgress = bootstrap.implementationProgress || null;
  const stockPendingItems = implementationProgress?.stockPendingItems;
  const hasActionableImplementation = bootstrapLoaded
    && Number.isFinite(stockPendingItems)
    && stockPendingItems > 0;
  const needsFirstItem = bootstrapLoaded && progress.itemCount === 0 && canCreateItems;
  const needsMoneySetup = bootstrapLoaded
    && canReadMoneyPlaces
    && !bootstrapping
    && bootstrap.registers.length === 0;
  const needsFirstMovement = bootstrapLoaded
    && !needsFirstItem
    && !needsMoneySetup
    && progress.confirmedMovementCount === 0;

  const initialOnboardingStage = needsFirstItem
    ? {
      key: "items",
      step: 1,
      icon: <CategoryRoundedIcon />,
      title: "Primero cargá los ítems del negocio",
      detail: "Creá productos, servicios, insumos y gastos. Son la base para que después una compra, venta o gasto tenga qué afectar.",
    }
    : needsMoneySetup
      ? {
        key: "money",
        step: 2,
        icon: <AccountBalanceWalletRoundedIcon />,
        title: "Decinos dónde entra la plata de tus ventas",
        detail: canManageMoneyPlaces
          ? "Puede ser efectivo, una cuenta bancaria, una billetera digital u otra caja que uses en el negocio."
          : "Necesitás que un administrador configure dónde se registra el dinero antes de empezar a operar.",
      }
      : needsFirstMovement
        ? {
          key: "operations",
          step: 3,
          icon: <ReceiptLongRoundedIcon />,
          title: "La base está lista: ya podés registrar la primera operación",
          detail: "A partir de acá registrá ventas, compras, gastos, cobros o transferencias. WebProfit irá actualizando caja, stock y contabilidad con cada confirmación.",
        }
        : { key: "ongoing" };

  const activeStockCount = implementationProgress?.activeStockCount || null;
  const totalItems = implementationProgress?.totalItems ?? progress.itemCount;
  const progressiveDetail = activeStockCount
    ? `Ya cargaste ${totalItems} ítems. Te quedan ${stockPendingItems} productos con stock pendiente. El conteo en curso lleva ${activeStockCount.countedLines} de ${activeStockCount.totalLines} existencias contadas; podés seguir usando WebProfit mientras lo completás.`
    : canManageStock
      ? `Ya cargaste ${totalItems} ítems. Te quedan ${stockPendingItems} productos con stock pendiente. Podés trabajar con lo que ya está listo y completar el inventario por etapas.`
      : `Ya cargaste ${totalItems} ítems. Te quedan ${stockPendingItems} productos con stock pendiente. Podés seguir trabajando; un usuario autorizado puede completar el inventario cuando corresponda.`;

  const onboardingStage = initialOnboardingStage.key === "ongoing" && hasActionableImplementation
    ? {
      key: "implementation",
      icon: <Inventory2RoundedIcon />,
      title: "Podés seguir trabajando mientras completás la implementación",
      detail: progressiveDetail,
    }
    : initialOnboardingStage;

  const onboardingActions = onboardingStage.key === "items"
    ? [
      canCreateItems
        ? { key: "items", label: "Elegir cómo empezar", primary: true, onClick: () => navigate(`${base}/items?inicio=1`) }
        : null,
    ].filter(Boolean)
    : onboardingStage.key === "money"
      ? [
        canManageMoneyPlaces
          ? { key: "cash", label: "Configurar caja y cuentas", primary: true, onClick: () => navigate(`${base}/caja?crear=1`) }
          : null,
      ].filter(Boolean)
      : onboardingStage.key === "operations"
        ? [
          canCreateSale || canCreatePurchase || canCreateExpense
            ? { key: "movement", label: "Registrar primera operación", primary: true, onClick: () => navigate(`${base}/movimientos?nuevo=1`) }
            : null,
          canReadLedger
            ? { key: "accounts", label: "Revisar cuentas", onClick: () => navigate(`${base}/cuentas`) }
            : null,
        ].filter(Boolean)
        : onboardingStage.key === "implementation"
          ? [
            canManageStock
              ? {
                  key: "stock",
                  label: activeStockCount
                    ? "Continuar conteo"
                    : implementationProgress?.stockCountAvailable
                      ? "Contar stock"
                      : "Completar stock",
                  primary: true,
                  onClick: () => navigate(
                    implementationProgress?.stockCountAvailable
                      ? `${base}/stock?conteo=1`
                      : `${base}/stock?inventario=inicial`,
                  ),
                }
              : null,
            canCreateItems
              ? { key: "add-items", label: "Agregar más ítems", onClick: () => navigate(`${base}/items?inicio=1`) }
              : null,
          ].filter(Boolean).slice(0, 2)
          : [];

  React.useEffect(() => {
    if (loading || !enabled || !visibleManagementTools.length) return;
    if (!visibleManagementTools.some((tool) => tool.key === currentManagementTool)) {
      const first = visibleManagementTools[0];
      navigate(first.path ? first.path : base, { replace: true });
    }
  }, [loading, enabled, visibleManagementTools, currentManagementTool, navigate, base]);

  React.useEffect(() => {
    if (
      loading ||
      currentManagementTool !== "accounting" ||
      !visibleAccountingTools.length
    ) return;
    if (!visibleAccountingTools.some((tool) => tool.path.endsWith(`/${currentAccountingTool}`))) {
      navigate(visibleAccountingTools[0].path, { replace: true });
    }
  }, [
    currentAccountingTool,
    currentManagementTool,
    loading,
    navigate,
    visibleAccountingTools,
  ]);

  if (loading) return <DashboardLoadingState label="Cargando gestión del negocio…" minHeight={360} />;

  if (!enabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Gestión del negocio"
        description="Este módulo incorpora ítems operativos, caja, inventario y contabilidad."
      />
    );
  }

  if (!visibleManagementTools.length) {
    return (
      <Alert severity="warning">
        Tu acceso está activo, pero no tenés permisos para consultar la gestión del negocio. Pedile al titular que revise tu rol.
      </Alert>
    );
  }

  return (
    <Stack spacing={{ xs: 1.25, md: 1.6 }} sx={{ width: "100%", minWidth: 0 }}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      <InitialSetupGuide stage={onboardingStage} actions={onboardingActions} />

      <Box sx={{ minWidth: 0 }}>
        <Outlet
          context={{
            project: resolvedProject,
            access,
            bootstrap,
            refreshBootstrap,
            setGlobalError: setError,
            can: (permission) => hasProjectPermission(access, permission),
          }}
        />
      </Box>
    </Stack>
  );
}
